import isArray from "lodash/isArray";
import mapValues from "lodash/mapValues";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { RouterPrompt } from "PFComponents/router_prompt/router_prompt";
import { Cover } from "PFCore/components/cover";
import { scroll } from "PFCore/helpers/scroll";
import { useConfirm } from "PFCore/helpers/use_confirm";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./legacy_form.module.scss";

type LegacyFormProps = {
  blockNavigationOnChange?: boolean;
  children?: React.ReactNode;
  growlErrors?: boolean;
  isDirty?: boolean;
  isNew?: boolean;
  isSubmitDisabled?: boolean;
  qaIdPrefix?: string;
  submitLabel?: string;
  onSubmit: (onSuccess: (...args: any[]) => void, onError: (...args: any[]) => void) => Promise<any> | void;
  onCancel?: () => void;
  onRemove?: () => void;
  onSuccess?: (...args: any[]) => void;
  onError?: (...args: any[]) => void;
};

export const LegacyForm = ({
  isNew = false,
  isDirty = false,
  blockNavigationOnChange = false,
  children,
  isSubmitDisabled,
  growlErrors,
  onCancel,
  onError,
  onRemove,
  onSubmit,
  onSuccess,
  qaIdPrefix = "form",
  submitLabel
}: LegacyFormProps) => {
  const { t } = useTranslation("core", { keyPrefix: "form" });
  const { showGrowl } = useGrowlStoreActions();

  const [locked, setLocked] = useState(false);

  const confirmNavigation = useConfirm(isDirty);
  const { getSingleErrorByType } = useResponseErrors();

  const growlError = (options: { message?: string } = {}) => {
    showGrowl({
      message: options.message || t("notSaved"),
      kind: "error",
      onClick: () => {
        scroll("[data-target-id='input_field_set__error']", { offset: -100 });
      }
    });
  };

  const handleSuccess = (...args) => {
    setLocked(false);

    onSuccess?.(...args);
  };

  const handleError = (resp, ...rest) => {
    setLocked(false);

    const errors = mapValues(
      getSingleErrorByType(resp?.response || resp, { camelizeFieldNames: true }),
      (val) => (isArray(val) ? val.join(", ") : val)
    );

    onError?.(errors, resp, ...rest);

    if (growlErrors) {
      growlError();
    }
  };

  const handleSubmit = () => {
    setLocked(true);

    const submitPromise = onSubmit(handleSuccess, handleError);

    if (!submitPromise) {
      return;
    }
    // If onSubmit returns a promise, we'll wait for it to resolve, and execute form handlers
    Promise.resolve(submitPromise).then(handleSuccess, handleError);
  };

  const handleCancel = () => {
    if (blockNavigationOnChange && isDirty && confirmNavigation()) {
      return;
    }

    return onCancel?.();
  };

  return (
    <div className={css.form}>
      <RouterPrompt when={blockNavigationOnChange && isDirty} />
      {locked && <Cover />}
      {children}

      <div className={css.modelFormActions}>
        {onRemove && (
          <Button
            text={isNew ? t("legacyForm.cancel") : t("legacyForm.remove")}
            kind={isNew ? "secondary" : "danger"}
            qaId={`${qaIdPrefix}-remove`}
            onClick={onRemove}
          />
        )}
        {onCancel && (
          <Button
            text={t("legacyForm.cancel")}
            kind={"secondary"}
            qaId={`${qaIdPrefix}-cancel`}
            onClick={handleCancel}
          />
        )}
        <Button
          kind="primary"
          onClick={handleSubmit}
          qaId={`${qaIdPrefix}-button`}
          disabled={isSubmitDisabled || !isDirty}
        >
          {submitLabel || (isNew ? t("legacyForm.create") : t("legacyForm.update"))}
        </Button>
      </div>
    </div>
  );
};
